import { ColumnDef, createColumnHelper } from "@tanstack/react-table";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { StatusColumn } from "src/datasets/AssembleDatasetModal/StatusColumn";
import { unknownToString } from "src/datasets/api/apiUtils";
import { HistoryDataRecord } from "src/datasets/api/types";
import { HeaderCell } from "src/dataTable/HeaderCell";
import { RawCell } from "src/dataTable/RawCell";
import { ResultCell } from "src/dataTable/ResultCell";
import { formatDate } from "src/utils/datetime";

type Args = {
  requestColumnNames: string[];
};

const columnHelper = createColumnHelper<HistoryDataRecord>();

export const TableColumns = ({ requestColumnNames }: Args) => {
  const getDateString = (datetime: string) => {
    return formatDate(datetime, "dd MMM yy HH:mm:ss");
  };

  return [
    columnHelper.accessor("response.metadata.decision_id", {
      id: "decision_id",
      size: 50,
      header: () => (
        <HeaderCell className="pl-3">
          <span className="inline-block">ID</span>
        </HeaderCell>
      ),
      cell: ({ cell }) => (
        <RawCell
          cell={<CopyTextIcon value={cell.getValue()} />}
          classNameOverrides="w-10 pl-2"
        />
      ),
    }),
    columnHelper.accessor("response.metadata.timestamp", {
      id: "timestamp",
      header: () => <HeaderCell>Time</HeaderCell>,
      cell: ({ cell }) => (
        <RawCell cell={<>{getDateString(cell.getValue())}</>} />
      ),
    }),
    columnHelper.accessor("response.metadata", {
      id: "status_code",
      header: () => (
        <HeaderCell>
          <span>Status</span>
        </HeaderCell>
      ),
      cell: ({ cell }) => {
        const { status_code: statusCode, type } = cell.getValue();
        const message = (
          <div className="flex items-center justify-between">
            <span className="justify-start">
              <StatusColumn statusCode={statusCode} type={type} />
            </span>
          </div>
        );

        return (
          <ResultCell
            cell={message}
            className="max-w-0 overflow-auto"
            objectDetailPosition="topRight"
          />
        );
      },
    }),
    columnHelper.accessor("response.metadata.type", {
      id: "type",
      header: () => <HeaderCell>Type</HeaderCell>,
      cell: ({ cell }) => <RawCell cell={cell} />,
    }),
    columnHelper.accessor("response.metadata.error", {
      id: "error",
      header: () => <HeaderCell>Error</HeaderCell>,
      cell: ({ cell }) => <RawCell cell={cell} />,
    }),
    columnHelper.accessor("response.metadata.entity_id", {
      id: "entity_id",
      header: () => <HeaderCell>Entity ID</HeaderCell>,
      cell: ({ cell }) => <RawCell cell={cell} />,
    }),
    ...requestColumnNames.map((column) =>
      columnHelper.accessor(`request.data.${column}`, {
        id: column,
        header: () => (
          <HeaderCell className="whitespace-nowrap">{column}</HeaderCell>
        ),
        cell: ({ cell }) => (
          <ResultCell
            cell={<>{unknownToString(cell.getValue())}</>}
            objectDetailPosition="topLeft"
          />
        ),
      }),
    ),
    columnHelper.accessor("response.metadata.version", {
      id: "version",
      header: () => (
        <HeaderCell className="whitespace-nowrap">Flow version ID</HeaderCell>
      ),
      cell: ({ cell }) => <RawCell cell={cell} />,
    }),
    columnHelper.accessor("response.metadata.traffic_policy_name", {
      id: "traffic_policy_name",
      header: () => (
        <HeaderCell className="whitespace-nowrap">Traffic policy</HeaderCell>
      ),
      cell: ({ cell }) => <RawCell cell={cell} />,
    }),
  ] as ColumnDef<HistoryDataRecord>[];
};
